import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaCircleCheck,
  FaCircleXmark,
  FaCertificate,
  FaChevronRight,
} from "react-icons/fa6";
import { toast } from "react-toastify";
import Header from "../components/Header";
import api from "../utils/axiosConfig";
import { downloadBlobFile } from "../utils/downloadBlob";
import { ExamView, SubmitExamResponse } from "../utils/entities/course";
import {
  CoursePageShell,
  CourseBreadcrumb,
  CourseStepper,
  CourseNavBar,
  CourseCard,
  CourseLoading,
  CourseConfirmModal,
} from "../components/courses/CourseUi";

const CourseExam: React.FC = () => {
  const { id, lessonId: lessonIdParam } = useParams<{
    id: string;
    lessonId: string;
  }>();
  const courseId = Number(id);
  const lessonId = Number(lessonIdParam);
  const isValid =
    Number.isFinite(courseId) &&
    courseId > 0 &&
    Number.isFinite(lessonId) &&
    lessonId > 0;
  const navigate = useNavigate();

  const [exam, setExam] = useState<ExamView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [result, setResult] = useState<SubmitExamResponse | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const examPath = `agent/courses/${courseId}/lessons/${lessonId}/exam`;

  const loadExam = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setAnswers({});
    setStep(0);
    setConfirmOpen(false);
    try {
      const { data } = await api.get<ExamView>(examPath);
      setExam(data);
    } catch (e) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Não foi possível carregar a prova.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [examPath]);

  useEffect(() => {
    if (isValid) {
      loadExam();
    } else {
      setLoading(false);
      setError("Curso ou aula inválidos.");
    }
  }, [isValid, loadExam]);

  const selectOption = (questionId: number, optionId: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  const handleDownloadCertificate = useCallback(async () => {
    try {
      await downloadBlobFile(
        `agent/courses/${courseId}/certificate/download`,
        `certificado-curso-${courseId}.pdf`
      );
    } catch {
      toast.error("Certificado ainda não disponível.");
    }
  }, [courseId]);

  const handleSubmit = async () => {
    if (!exam || submittingRef.current) return;
    if (Object.keys(answers).length < exam.questions.length) {
      toast.warn("Responda todas as questões antes de enviar.");
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setConfirmOpen(false);
    try {
      const payload = {
        answers: exam.questions.map((q) => ({
          questionId: q.id,
          selectedOptionId: answers[q.id],
        })),
      };
      const { data } = await api.post<SubmitExamResponse>(examPath, payload);
      setResult(data);
      if (data.passed) {
        toast.success(
          data.courseCompleted
            ? "Parabéns! Você concluiu o curso."
            : "Parabéns! Você foi aprovado na prova da aula."
        );
      } else if (data.examLocked) {
        toast.error("Tentativas esgotadas. A prova desta aula está bloqueada.");
      } else {
        const remaining = data.attemptsRemaining ?? 0;
        toast.error(
          remaining > 0
            ? `Você não atingiu a nota mínima. Restam ${remaining} tentativa${
                remaining === 1 ? "" : "s"
              }.`
            : "Você não atingiu a nota mínima."
        );
      }
    } catch (e) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Erro ao enviar a prova.";
      toast.error(msg);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const goNext = () => {
    if (!exam || submittingRef.current) return;
    const question = exam.questions[step];
    if (answers[question.id] === undefined) {
      toast.warn("Selecione uma alternativa para continuar.");
      return;
    }
    if (step < exam.questions.length - 1) {
      setStep((s) => s + 1);
    } else {
      setConfirmOpen(true);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <CourseLoading message="Carregando prova..." />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <CoursePageShell>
          <CourseBreadcrumb
            items={[
              { label: "Cursos", to: "/courses" },
              { label: "Curso", to: `/courses/${courseId}` },
              { label: "Prova da aula" },
            ]}
          />
          <p className="text-red-600">{error}</p>
        </CoursePageShell>
      </>
    );
  }

  if (result) {
    const maxAttempts = result.maxAttempts ?? 3;
    const attemptsUsed = result.attemptsUsed ?? 0;
    const attemptsRemaining = result.attemptsRemaining ?? 0;
    const locked = !!result.examLocked;
    return (
      <>
        <Header />
        <CoursePageShell>
          <CourseBreadcrumb
            items={[
              { label: "Cursos", to: "/courses" },
              { label: "Curso", to: `/courses/${courseId}` },
              { label: "Resultado" },
            ]}
          />
          <CourseCard className="text-center max-w-2xl mx-auto">
            {result.passed ? (
              <FaCircleCheck className="text-emerald-500 text-4xl mx-auto mb-3" />
            ) : (
              <FaCircleXmark className="text-red-500 text-4xl mx-auto mb-3" />
            )}
            <h1 className="text-xl font-bold text-slate-900 tracking-tight mb-2">
              {result.passed
                ? result.courseCompleted
                  ? "Curso concluído!"
                  : "Aula aprovada!"
                : locked
                ? "Tentativas esgotadas"
                : "Não aprovado"}
            </h1>
            <p className="text-slate-600 text-sm mb-1">
              Você acertou {result.correctAnswers} de {result.totalQuestions}{" "}
              questões
            </p>
            <p className="text-3xl font-bold text-slate-900 my-4 tabular-nums">
              {result.score}%
            </p>
            <p className="text-sm text-slate-500 mb-2">
              Nota mínima para aprovação: {result.passingScore}%
            </p>
            <p className="text-sm text-slate-500 mb-6 tabular-nums">
              Tentativa {attemptsUsed} de {maxAttempts}
              {!result.passed && !locked
                ? ` · ${attemptsRemaining} restante${
                    attemptsRemaining === 1 ? "" : "s"
                  }`
                : null}
            </p>
            {locked && (
              <p className="text-sm text-red-600 mb-6 max-w-md mx-auto">
                Você esgotou as {maxAttempts} tentativas permitidas. A prova
                desta aula está bloqueada permanentemente.
              </p>
            )}
            <div className="flex flex-wrap gap-2.5 justify-center">
              {result.passed && result.courseCompleted && (
                <button
                  onClick={handleDownloadCertificate}
                  className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 text-sm font-semibold shadow-sm transition-all"
                >
                  <FaCertificate className="text-sm" /> Baixar certificado
                </button>
              )}
              {result.passed && !result.courseCompleted && (
                <button
                  onClick={() => navigate(`/courses/${courseId}`)}
                  className="inline-flex items-center gap-2 bg-preppoBlue text-white px-4 py-2 rounded-lg hover:bg-preppoBlue/90 text-sm font-semibold shadow-sm transition-all"
                >
                  Continuar curso
                  <FaChevronRight className="text-xs" />
                </button>
              )}
              {!result.passed && !locked && (
                <button
                  onClick={loadExam}
                  className="bg-preppoBlue text-white px-4 py-2 rounded-lg hover:bg-preppoBlue/90 text-sm font-semibold shadow-sm transition-all"
                >
                  Refazer prova
                </button>
              )}
              <button
                onClick={() => navigate(`/courses/${courseId}`)}
                className="border border-slate-300 text-slate-800 px-4 py-2 rounded-lg hover:bg-slate-50 text-sm font-semibold transition-all"
              >
                Voltar ao curso
              </button>
            </div>
          </CourseCard>
        </CoursePageShell>
      </>
    );
  }

  const total = exam?.questions.length ?? 0;
  const question = exam?.questions[step];
  const isLast = step >= total - 1;
  const hasSelection = !!question && answers[question.id] !== undefined;
  const nextDisabled = submitting || !hasSelection;

  return (
    <>
      <Header />
      <CoursePageShell withStickyNav>
        <CourseBreadcrumb
          items={[
            { label: "Cursos", to: "/courses" },
            { label: "Curso", to: `/courses/${courseId}` },
            {
              label: exam?.lessonTitle
                ? `Prova · ${exam.lessonTitle}`
                : "Prova da aula",
            },
          ]}
        />

        <header className="mb-5 rounded-xl border border-slate-200 bg-white px-4 py-4 md:px-5 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">
                Prova da aula
              </h1>
              {exam?.lessonTitle && (
                <p className="text-sm text-slate-600 mt-1">{exam.lessonTitle}</p>
              )}
              {total > 0 && (
                <p className="text-xs text-slate-500 mt-1 tabular-nums">
                  Questão {step + 1} de {total}
                </p>
              )}
            </div>
            {exam && (
              <div className="flex flex-col items-end gap-1.5 self-start">
                <p className="inline-flex items-center rounded-md bg-slate-50 px-2.5 py-1 text-xs text-slate-600 border border-slate-200/80">
                  Nota mínima{" "}
                  <span className="ml-1 font-semibold text-slate-800 tabular-nums">
                    {exam.passingScore}%
                  </span>
                </p>
                {exam.maxAttempts != null && (
                  <p className="inline-flex items-center rounded-md bg-slate-50 px-2.5 py-1 text-xs text-slate-600 border border-slate-200/80 tabular-nums">
                    Tentativa {(exam.attemptsUsed ?? 0) + 1} de{" "}
                    {exam.maxAttempts}
                  </p>
                )}
              </div>
            )}
          </div>

          <CourseStepper
            variant="dots"
            total={total}
            currentIndex={step}
            doneFlags={exam?.questions.map((q) => answers[q.id] !== undefined)}
            onStepClick={(index) => {
              if (!exam) return;
              const target = exam.questions[index];
              const answered = answers[target.id] !== undefined;
              if (answered || index <= step) {
                setStep(index);
              }
            }}
          />
        </header>

        {question && (
          <CourseCard className="min-h-[260px]">
            <p className="font-semibold text-slate-900 mb-4 text-base leading-relaxed tracking-tight">
              {question.statement}
            </p>
            <div className="space-y-2.5">
              {question.options.map((option) => {
                const checked = answers[question.id] === option.id;
                return (
                  <label
                    key={option.id}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                      checked
                        ? "border-preppoBlue bg-preppoBlue/5 shadow-sm"
                        : "border-slate-200 hover:bg-slate-50/80 hover:border-slate-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name={`question-${question.id}`}
                      checked={checked}
                      onChange={() => selectOption(question.id, option.id)}
                      className="w-4 h-4 accent-preppoBlue"
                    />
                    <span
                      className={`text-sm ${
                        checked
                          ? "font-medium text-slate-900"
                          : "text-slate-700"
                      }`}
                    >
                      {option.text}
                    </span>
                  </label>
                );
              })}
            </div>
          </CourseCard>
        )}

        <CourseNavBar
          onPrev={() => setStep((s) => Math.max(0, s - 1))}
          prevDisabled={step === 0}
          prevDisabledTooltip="Esta é a primeira questão"
          onNext={goNext}
          nextDisabled={nextDisabled}
          nextDisabledTooltip={
            !hasSelection
              ? "Selecione uma alternativa para continuar"
              : undefined
          }
          nextLabel={
            submitting
              ? "Enviando..."
              : isLast
              ? "Enviar respostas"
              : "Próxima"
          }
        />
      </CoursePageShell>

      <CourseConfirmModal
        open={confirmOpen}
        title="Revisar respostas antes de enviar?"
        description="Ao enviar, suas respostas serão corrigidas imediatamente. Confira se todas as alternativas estão corretas."
        confirmLabel="Enviar prova"
        cancelLabel="Revisar"
        onConfirm={() => void handleSubmit()}
        onCancel={() => setConfirmOpen(false)}
        busy={submitting}
      />
    </>
  );
};

export default CourseExam;
